import { jsPDF } from 'jspdf';
import { drawPdfHeader } from './pdf-branding.util';

/**
 * Gemeinsamer, neu gestalteter PDF-Builder für Wartungs-/Einsatz-Berichte.
 * Wird vom Report-Dialog, der Auftrags-Detailseite und dem Einsatz-Formular
 * verwendet, damit überall derselbe (aufgeräumte) Bericht herauskommt.
 */

export interface ReportInfoEntry {
  label: string;
  value: string;
}

export interface ReportProblem {
  description: string;
  solution?: string | null;
}

export interface ReportChecklistItem {
  description: string;
  isChecked: boolean;
  /** Geprüft, aber NICHT in Ordnung → roter statt grüner Haken. */
  isNotOk?: boolean;
  remarks?: string | null;
  comments?: string[];
}

export interface ReportChecklist {
  name: string;
  items: ReportChecklistItem[];
}

export interface ReportMaterial {
  name: string;
  quantity: number;
  unit: string;
}

export interface ReportRegieLine {
  label: string;
  hours: number;
  hourlyRate: number;
}

export interface ReportSignature {
  label: string;
  name?: string | null;
  imageDataUrl?: string | null;
}

export interface ServiceReportData {
  title: string;               // z.B. 'Einsatz-Report' oder 'Wartungs-Report'
  subline: string;             // z.B. 'Erstellt am: 21.07.2026'
  info: ReportInfoEntry[];     // Auftragsinformationen (Kunde, Anlage, ...)
  initialSituation?: string | null;
  problems?: ReportProblem[];
  workDescription?: string | null;
  workItems?: string[];
  checklists?: ReportChecklist[];
  materials?: ReportMaterial[];
  regieLines?: ReportRegieLine[];
  photos?: string[];           // Data-URLs
  additionalNotes?: string | null;
  signatures?: ReportSignature[];
}

// Farbwelt des Berichts (abgestimmt auf den ELIN-Header).
const COLOR = {
  primary: [0, 95, 155] as const,
  ink: [26, 26, 46] as const,
  muted: [95, 104, 118] as const,
  faint: [150, 158, 168] as const,
  line: [214, 222, 232] as const,
  bgSoft: [244, 247, 252] as const,
  zebra: [249, 251, 254] as const,
  ok: [46, 125, 50] as const,
  notOk: [211, 47, 47] as const,
  open: [176, 132, 0] as const
};

const M = 14;                 // Seitenrand
const FOOTER_SPACE = 16;      // Platz für die Fußzeile

function pageWidth(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth();
}

function pageHeight(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight();
}

function contentWidth(doc: jsPDF): number {
  return pageWidth(doc) - 2 * M;
}

function bottomLimit(doc: jsPDF): number {
  return pageHeight(doc) - FOOTER_SPACE;
}

/** Sorgt für genügend Platz; bricht sonst auf eine neue Seite um. */
function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > bottomLimit(doc)) {
    doc.addPage();
    return 18;
  }
  return y;
}

/** Abschnittstitel mit blauem Akzentbalken und feiner Linie. */
function sectionTitle(doc: jsPDF, y: number, title: string): number {
  y = ensureSpace(doc, y, 18);
  y += 3;
  doc.setFillColor(...COLOR.primary);
  doc.rect(M, y - 3.6, 1.6, 5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.setTextColor(...COLOR.ink);
  doc.text(title, M + 4.5, y);
  doc.setDrawColor(...COLOR.line);
  doc.setLineWidth(0.25);
  doc.line(M, y + 2.2, pageWidth(doc) - M, y + 2.2);
  return y + 8;
}

/** Mehrzeiliger Fließtext, gibt neues y zurück (mit Seitenumbruch je Zeile). */
function paragraph(doc: jsPDF, y: number, text: string, options?: {
  x?: number; width?: number; size?: number; color?: readonly number[]; style?: string; lineHeight?: number;
}): number {
  const x = options?.x ?? M;
  const width = options?.width ?? (pageWidth(doc) - M - x);
  const size = options?.size ?? 9.5;
  const lineHeight = options?.lineHeight ?? size * 0.46;
  doc.setFont('helvetica', options?.style ?? 'normal');
  doc.setFontSize(size);
  const c = options?.color ?? COLOR.ink;
  doc.setTextColor(c[0], c[1], c[2]);
  const lines: string[] = doc.splitTextToSize(text, width);
  for (const line of lines) {
    y = ensureSpace(doc, y, lineHeight);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

/** Auftragsinformationen als zweispaltiges Raster in einer hellen Box. */
function infoGrid(doc: jsPDF, y: number, entries: ReportInfoEntry[]): number {
  const filtered = entries.filter(e => (e.value ?? '').toString().trim() && e.value !== '–');
  if (filtered.length === 0) return y;

  const cw = contentWidth(doc);
  const colW = cw / 2;
  const rowH = 9.5;
  const rows = Math.ceil(filtered.length / 2);
  const boxH = rows * rowH + 6;

  y = ensureSpace(doc, y, boxH + 4);

  doc.setFillColor(...COLOR.bgSoft);
  doc.roundedRect(M, y, cw, boxH, 2, 2, 'F');

  for (let i = 0; i < filtered.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + 5 + col * colW;
    const rowY = y + 7 + row * rowH;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(...COLOR.muted);
    doc.text(filtered[i].label.replace(/:$/, '').toUpperCase(), x, rowY - 3.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...COLOR.ink);
    const value = doc.splitTextToSize(filtered[i].value, colW - 10)[0] ?? '';
    doc.text(value, x, rowY + 1);
  }

  return y + boxH + 6;
}

/** Gezeichnetes Häkchen/Kreis statt nicht darstellbarer Unicode-Symbole. */
function drawCheckSymbol(doc: jsPDF, x: number, y: number, state: 'ok' | 'notOk' | 'open'): void {
  const r = 1.9;
  if (state === 'open') {
    doc.setDrawColor(...COLOR.faint);
    doc.setLineWidth(0.4);
    doc.circle(x, y, r, 'S');
    return;
  }
  if (state === 'ok') {
    // Grüner Kreis mit weißem Haken
    doc.setFillColor(...COLOR.ok);
    doc.circle(x, y, r, 'F');
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.45);
    doc.line(x - 0.9, y + 0.1, x - 0.25, y + 0.85);
    doc.line(x - 0.25, y + 0.85, x + 1.0, y - 0.75);
    return;
  }
  // Nicht OK: roter Kreis mit weißem X
  doc.setFillColor(...COLOR.notOk);
  doc.circle(x, y, r, 'F');
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.45);
  doc.line(x - 0.85, y - 0.85, x + 0.85, y + 0.85);
  doc.line(x - 0.85, y + 0.85, x + 0.85, y - 0.85);
}

/** Kopfzeile einer Tabelle. */
function tableHeader(doc: jsPDF, y: number, columns: { text: string; x: number; width: number; align?: 'left' | 'right' }[]): number {
  y = ensureSpace(doc, y, 12);
  const cw = contentWidth(doc);
  doc.setFillColor(...COLOR.primary);
  doc.rect(M, y, cw, 6.4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  for (const col of columns) {
    const tx = col.align === 'right' ? col.x + col.width - 2 : col.x + 2;
    doc.text(col.text, tx, y + 4.3, { align: col.align === 'right' ? 'right' : 'left' });
  }
  return y + 6.4;
}

function num(value: number, digits = 2): string {
  return (Number(value) || 0).toLocaleString('de-AT', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function euro(value: number): string {
  return (Number(value) || 0).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
}

/** Checklisten mit Status, Bemerkung und Kommentaren. */
function checklistsSection(doc: jsPDF, y: number, checklists: ReportChecklist[]): number {
  const total = checklists.reduce((s, cl) => s + cl.items.length, 0);
  const done = checklists.reduce((s, cl) => s + cl.items.filter(i => i.isChecked || i.isNotOk).length, 0);
  const notOkTotal = checklists.reduce((s, cl) => s + cl.items.filter(i => i.isNotOk).length, 0);
  const suffix = notOkTotal > 0 ? `, ${notOkTotal} nicht OK` : '';
  y = sectionTitle(doc, y, `Checklisten (${done}/${total} geprüft${suffix})`);

  const cw = contentWidth(doc);
  for (const cl of checklists) {
    y = ensureSpace(doc, y, 16);

    // Kopf der einzelnen Checkliste
    doc.setFillColor(...COLOR.bgSoft);
    doc.roundedRect(M, y, cw, 6.4, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...COLOR.ink);
    doc.text(cl.name || 'Checkliste', M + 3, y + 4.4);
    const clDone = cl.items.filter(i => i.isChecked || i.isNotOk).length;
    const clNotOk = cl.items.filter(i => i.isNotOk).length;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.muted);
    const clSuffix = clNotOk > 0 ? `, ${clNotOk} nicht OK` : '';
    doc.text(`${clDone}/${cl.items.length} geprüft${clSuffix}`, M + cw - 3, y + 4.4, { align: 'right' });
    y += 8.5;

    let zebra = false;
    for (const item of cl.items) {
      const textX = M + 10;
      const textW = cw - 14;

      doc.setFontSize(9);
      const descLines: string[] = doc.splitTextToSize(item.description, textW);
      const extraLines: { text: string; italic: boolean }[] = [];
      if (item.remarks && item.remarks.trim()) {
        for (const l of doc.splitTextToSize(`Bemerkung: ${item.remarks.trim()}`, textW - 4) as string[]) {
          extraLines.push({ text: l, italic: true });
        }
      }
      for (const comment of item.comments ?? []) {
        if (!comment.trim()) continue;
        for (const l of doc.splitTextToSize(`Kommentar: ${comment.trim()}`, textW - 4) as string[]) {
          extraLines.push({ text: l, italic: true });
        }
      }

      const rowH = 2.5 + descLines.length * 4.2 + extraLines.length * 3.9 + 2;
      y = ensureSpace(doc, y, rowH);

      if (zebra) {
        doc.setFillColor(...COLOR.zebra);
        doc.rect(M, y - 1, cw, rowH, 'F');
      }
      zebra = !zebra;

      drawCheckSymbol(doc, M + 4, y + 2.2, item.isChecked ? 'ok' : item.isNotOk ? 'notOk' : 'open');

      let lineY = y + 3.4;
      doc.setFont('helvetica', item.isNotOk ? 'bold' : 'normal');
      doc.setFontSize(9);
      if (item.isNotOk) {
        doc.setTextColor(...COLOR.notOk);
      } else {
        doc.setTextColor(...COLOR.ink);
      }
      for (const l of descLines) {
        doc.text(l, textX, lineY);
        lineY += 4.2;
      }
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...COLOR.muted);
      for (const l of extraLines) {
        doc.text(l.text, textX + 4, lineY - 0.6);
        lineY += 3.9;
      }
      y += rowH;
    }
    y += 4;
  }
  return y;
}

function materialsSection(doc: jsPDF, y: number, materials: ReportMaterial[]): number {
  y = sectionTitle(doc, y, 'Verwendetes Material');
  const cw = contentWidth(doc);
  const cols = [
    { text: 'Material', x: M, width: cw - 50 },
    { text: 'Menge', x: M + cw - 50, width: 25, align: 'right' as const },
    { text: 'Einheit', x: M + cw - 25, width: 25 }
  ];
  y = tableHeader(doc, y, cols);

  let zebra = false;
  for (const mat of materials) {
    doc.setFontSize(9);
    const nameLines: string[] = doc.splitTextToSize(mat.name, cols[0].width - 6);
    const rowH = nameLines.length * 4.2 + 2.6;
    y = ensureSpace(doc, y, rowH);
    if (zebra) {
      doc.setFillColor(...COLOR.zebra);
      doc.rect(M, y, cw, rowH, 'F');
    }
    zebra = !zebra;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.ink);
    let ty = y + 4.2;
    for (const l of nameLines) {
      doc.text(l, M + 2, ty);
      ty += 4.2;
    }
    doc.text(num(mat.quantity, mat.quantity % 1 === 0 ? 0 : 2), cols[1].x + cols[1].width - 2, y + 4.2, { align: 'right' });
    doc.text(mat.unit || 'Stk', cols[2].x + 2, y + 4.2);
    y += rowH;
  }
  doc.setDrawColor(...COLOR.line);
  doc.setLineWidth(0.25);
  doc.line(M, y, M + cw, y);
  return y + 6;
}

function regieSection(doc: jsPDF, y: number, lines: ReportRegieLine[]): number {
  y = sectionTitle(doc, y, 'Regiestunden');
  const cw = contentWidth(doc);
  const cols = [
    { text: 'Leistung', x: M, width: cw - 90 },
    { text: 'Stunden', x: M + cw - 90, width: 25, align: 'right' as const },
    { text: 'Satz', x: M + cw - 65, width: 30, align: 'right' as const },
    { text: 'Summe', x: M + cw - 35, width: 35, align: 'right' as const }
  ];
  y = tableHeader(doc, y, cols);

  let zebra = false;
  let totalHours = 0;
  let totalAmount = 0;
  for (const line of lines) {
    const hours = Number(line.hours) || 0;
    const rate = Number(line.hourlyRate) || 0;
    totalHours += hours;
    totalAmount += hours * rate;

    doc.setFontSize(9);
    const labelLines: string[] = doc.splitTextToSize(line.label || '–', cols[0].width - 6);
    const rowH = labelLines.length * 4.2 + 2.6;
    y = ensureSpace(doc, y, rowH + 8);
    if (zebra) {
      doc.setFillColor(...COLOR.zebra);
      doc.rect(M, y, cw, rowH, 'F');
    }
    zebra = !zebra;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR.ink);
    let ty = y + 4.2;
    for (const l of labelLines) {
      doc.text(l, M + 2, ty);
      ty += 4.2;
    }
    doc.text(num(hours), cols[1].x + cols[1].width - 2, y + 4.2, { align: 'right' });
    doc.text(euro(rate), cols[2].x + cols[2].width - 2, y + 4.2, { align: 'right' });
    doc.text(euro(hours * rate), cols[3].x + cols[3].width - 2, y + 4.2, { align: 'right' });
    y += rowH;
  }

  // Summenzeile
  doc.setDrawColor(...COLOR.line);
  doc.setLineWidth(0.25);
  doc.line(M, y, M + cw, y);
  y += 4.6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLOR.ink);
  doc.text('Gesamt', M + 2, y);
  doc.text(num(totalHours), cols[1].x + cols[1].width - 2, y, { align: 'right' });
  doc.text(euro(totalAmount), cols[3].x + cols[3].width - 2, y, { align: 'right' });
  return y + 7;
}

function problemsSection(doc: jsPDF, y: number, problems: ReportProblem[]): number {
  y = sectionTitle(doc, y, 'Festgestellte Probleme & Lösungen');
  for (let i = 0; i < problems.length; i++) {
    const p = problems[i];
    y = ensureSpace(doc, y, 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLOR.primary);
    doc.text(`Problem ${i + 1}`, M, y);
    y += 4.4;
    y = paragraph(doc, y, p.description || '–', { x: M + 4, size: 9 });
    if (p.solution && p.solution.trim()) {
      y += 1;
      y = ensureSpace(doc, y, 8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLOR.ok);
      doc.text('Lösung', M + 4, y);
      y += 4.4;
      y = paragraph(doc, y, p.solution.trim(), { x: M + 8, size: 9 });
    }
    y += 3.5;
  }
  return y;
}

async function photosSection(doc: jsPDF, y: number, photos: string[]): Promise<number> {
  y = sectionTitle(doc, y, 'Fotodokumentation');
  const cw = contentWidth(doc);
  const gap = 6;
  const imgW = (cw - gap) / 2;
  const imgH = imgW * 0.72;

  let x = M;
  for (const dataUrl of photos) {
    if (!dataUrl) continue;
    y = (x === M) ? ensureSpace(doc, y, imgH + 6) : y;
    try {
      // Rahmen + Bild (Bild leicht eingerückt, damit der Rahmen sichtbar bleibt)
      doc.setDrawColor(...COLOR.line);
      doc.setLineWidth(0.3);
      doc.rect(x, y, imgW, imgH, 'S');
      try {
        doc.addImage(dataUrl, 'JPEG', x + 0.6, y + 0.6, imgW - 1.2, imgH - 1.2);
      } catch {
        doc.addImage(dataUrl, 'PNG', x + 0.6, y + 0.6, imgW - 1.2, imgH - 1.2);
      }
    } catch {
      // Foto nicht lesbar – Platzhalter überspringen
    }
    if (x === M) {
      x = M + imgW + gap;
    } else {
      x = M;
      y += imgH + gap;
    }
  }
  if (x !== M) {
    y += imgH + gap;
  }
  return y + 2;
}

function signaturesSection(doc: jsPDF, y: number, signatures: ReportSignature[]): number {
  const cw = contentWidth(doc);
  const boxW = signatures.length > 1 ? (cw - 6) / 2 : cw;
  const boxH = 42;

  y = sectionTitle(doc, y, 'Unterschriften');
  y = ensureSpace(doc, y, boxH + 4);

  let x = M;
  for (let i = 0; i < signatures.length; i++) {
    const sig = signatures[i];
    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, boxW, boxH, 2, 2, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.muted);
    doc.text(sig.label.toUpperCase(), x + 4, y + 6);

    if (sig.imageDataUrl) {
      try {
        doc.addImage(sig.imageDataUrl, 'PNG', x + 4, y + 8, boxW - 8, 22);
      } catch {
        // Unterschrift nicht lesbar – Feld bleibt leer
      }
    }

    doc.setDrawColor(...COLOR.faint);
    doc.setLineWidth(0.25);
    doc.line(x + 4, y + boxH - 9, x + boxW - 4, y + boxH - 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLOR.ink);
    doc.text(sig.name?.trim() || '–', x + 4, y + boxH - 4);

    if (signatures.length > 1 && i % 2 === 0) {
      x = M + boxW + 6;
    } else {
      x = M;
      if (i < signatures.length - 1) {
        y += boxH + 6;
        y = ensureSpace(doc, y, boxH + 4);
      }
    }
  }
  return y + boxH + 6;
}

/** Fußzeile auf allen Seiten (nachträglich, damit die Gesamtseitenzahl stimmt). */
function drawFooters(doc: jsPDF, title: string): void {
  const pages = doc.getNumberOfPages();
  const createdText = `${title} · erstellt am ${new Date().toLocaleDateString('de-AT')}`;
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const pw = pageWidth(doc);
    const ph = pageHeight(doc);
    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.25);
    doc.line(M, ph - 11, pw - M, ph - 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...COLOR.faint);
    doc.text(createdText, M, ph - 7);
    doc.text(`Seite ${i} von ${pages}`, pw - M, ph - 7, { align: 'right' });
  }
}

/** Baut den kompletten Bericht und liefert das jsPDF-Dokument zurück. */
export async function buildServiceReportPdf(data: ServiceReportData): Promise<jsPDF> {
  const doc = new jsPDF();

  let y = await drawPdfHeader(doc, data.title, data.subline);

  // Auftragsinformationen
  y = sectionTitle(doc, y, 'Auftragsinformationen');
  y = infoGrid(doc, y, data.info);

  if (data.initialSituation && data.initialSituation.trim()) {
    y = sectionTitle(doc, y, 'Ist-Zustand bei Ankunft');
    y = paragraph(doc, y, data.initialSituation.trim());
    y += 4;
  }

  if (data.problems && data.problems.length > 0) {
    y = problemsSection(doc, y, data.problems);
    y += 2;
  }

  const hasWork = (data.workDescription && data.workDescription.trim()) || (data.workItems && data.workItems.length > 0);
  if (hasWork) {
    y = sectionTitle(doc, y, 'Durchgeführte Arbeiten');
    if (data.workDescription && data.workDescription.trim()) {
      y = paragraph(doc, y, data.workDescription.trim());
      y += 2;
    }
    const items = data.workItems ?? [];
    for (let i = 0; i < items.length; i++) {
      y = ensureSpace(doc, y, 6);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLOR.primary);
      doc.text(`${i + 1}.`, M, y);
      y = paragraph(doc, y, items[i], { x: M + 7, size: 9.5 });
      y += 1;
    }
    y += 4;
  }

  if (data.checklists && data.checklists.length > 0) {
    y = checklistsSection(doc, y, data.checklists);
  }

  if (data.regieLines && data.regieLines.length > 0) {
    y = regieSection(doc, y, data.regieLines);
  }

  if (data.materials && data.materials.length > 0) {
    y = materialsSection(doc, y, data.materials);
  }

  if (data.photos && data.photos.length > 0) {
    y = await photosSection(doc, y, data.photos);
  }

  if (data.additionalNotes && data.additionalNotes.trim()) {
    y = sectionTitle(doc, y, 'Zusätzliche Anmerkungen');
    y = paragraph(doc, y, data.additionalNotes.trim());
    y += 4;
  }

  if (data.signatures && data.signatures.length > 0) {
    signaturesSection(doc, y, data.signatures);
  }

  drawFooters(doc, data.title);
  return doc;
}
